'use client';

import {
  createContext,
  use,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

import {
  GA_CONSENT_CHOICE,
  GA_CONSENT_SOURCE,
} from '@/constants/analytics';
import {
  COOKIES_ANALYTICS_CONSENT_KEY,
  COOKIES_CONSENT_CHANGE_EVENT,
  COOKIES_CONSENT_VALUE,
  type CookiesConsentRecord,
  type CookiesConsentValue,
} from '@/constants/cookies';
import { isAnalyticsActive } from '@/lib/analytics/config';
import { trackCookieConsent } from '@/lib/analytics/trackEvent';
import {
  readAnalyticsConsentRecord,
  syncAnalyticsConsentCacheFromStorage,
  writeAnalyticsConsent,
} from '@/lib/cookieConsent';

type CookieConsentContextValue = {
  consent: CookiesConsentValue | null;
  /** Full localStorage record; `id` / `decidedAt` null for legacy plain-string entries. */
  record: CookiesConsentRecord | null;
  /** True after the first client read of localStorage. */
  ready: boolean;
  isBannerOpen: boolean;
  openBanner: () => void;
  acceptAnalytics: () => void;
  rejectAnalytics: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function subscribeConsent(onStoreChange: () => void) {
  const onConsentChange = () => {
    onStoreChange();
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== COOKIES_ANALYTICS_CONSENT_KEY) {
      return;
    }
    // Other tabs mutate localStorage; refresh the stable snapshot before React reads.
    syncAnalyticsConsentCacheFromStorage();
    onStoreChange();
  };

  window.addEventListener(COOKIES_CONSENT_CHANGE_EVENT, onConsentChange);
  window.addEventListener('storage', onStorage);

  return () => {
    window.removeEventListener(COOKIES_CONSENT_CHANGE_EVENT, onConsentChange);
    window.removeEventListener('storage', onStorage);
  };
}

function getConsentRecordSnapshot(): CookiesConsentRecord | null {
  return readAnalyticsConsentRecord();
}

function getConsentRecordServerSnapshot(): CookiesConsentRecord | null {
  return null;
}

/** Client-only flag without setState-in-effect (matches SSR `false`, then `true`). */
function subscribeIsClient() {
  return () => {};
}

function getIsClientSnapshot() {
  return true;
}

function getIsClientServerSnapshot() {
  return false;
}

type CookieConsentProviderProps = {
  children: ReactNode;
};

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const ready = useSyncExternalStore(
    subscribeIsClient,
    getIsClientSnapshot,
    getIsClientServerSnapshot,
  );
  const record = useSyncExternalStore(
    subscribeConsent,
    getConsentRecordSnapshot,
    getConsentRecordServerSnapshot,
  );
  const consent = record?.consent ?? null;
  const [manualOpen, setManualOpen] = useState(false);
  const analyticsActive = isAnalyticsActive();

  // No consent UI when analytics is fully off — nothing to grant or deny.
  const isBannerOpen =
    analyticsActive && (manualOpen || (ready && consent === null));

  const openBanner = () => {
    if (!analyticsActive) return;
    setManualOpen(true);
  };

  const acceptAnalytics = () => {
    // Capture before closing: manualOpen means settings/manage-preferences.
    const source = manualOpen
      ? GA_CONSENT_SOURCE.settings
      : GA_CONSENT_SOURCE.banner;
    writeAnalyticsConsent(COOKIES_CONSENT_VALUE.granted);
    setManualOpen(false);
    trackCookieConsent({
      choice: GA_CONSENT_CHOICE.granted,
      source,
    });
  };

  const rejectAnalytics = () => {
    const source = manualOpen
      ? GA_CONSENT_SOURCE.settings
      : GA_CONSENT_SOURCE.banner;
    // Fire before writing denied so gtag (if loaded) can still send.
    trackCookieConsent({
      choice: GA_CONSENT_CHOICE.denied,
      source,
    });
    writeAnalyticsConsent(COOKIES_CONSENT_VALUE.denied);
    setManualOpen(false);
  };

  return (
    <CookieConsentContext
      value={{
        consent,
        record,
        ready,
        isBannerOpen,
        openBanner,
        acceptAnalytics,
        rejectAnalytics,
      }}
    >
      {children}
    </CookieConsentContext>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const value = use(CookieConsentContext);
  if (!value) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return value;
}
